import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { errorText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FormError, PasswordInput } from "./Fields";
import { StepTitle } from "./LoginShell";

export const PASSWORD_RULE = "At least 12 characters. A few unrelated words work well.";

// New password: after a bootstrap / reset (must_change_password), or from a set-password link (no current password).
export const NewPasswordForm = ({ title, intro, needsCurrent, onSubmit, submitLabel = "Save my password" }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (next !== again) return setError("The two new passwords don't match.");
    setBusy(true);
    setError("");
    try {
      await onSubmit(next, current);
    } catch (err) {
      setError(errorText(err, "Couldn't save the password. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="new-password-form">
      <StepTitle title={title}>{intro}</StepTitle>
      {needsCurrent && <PasswordInput id="pw-current" testid="pw-current-input" label="Current password" value={current} onChange={setCurrent} />}
      <PasswordInput id="pw-new" testid="pw-new-input" label="New password" autoComplete="new-password" value={next} onChange={setNext} />
      <PasswordInput id="pw-again" testid="pw-again-input" label="New password again" autoComplete="new-password" value={again} onChange={setAgain} />
      <p data-testid="pw-rule" className="text-xs text-[#6b6b73]">{PASSWORD_RULE}</p>
      <FormError text={error} />
      <Button data-testid="pw-submit-btn" type="submit" disabled={busy || next.length < 12 || !again || (needsCurrent && !current)} className="h-12 w-full rounded-full font-bold gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} {submitLabel}
      </Button>
    </form>
  );
};

export const ChangePassword = ({ onDone }) => {
  const { changePassword } = useAuth();
  return (
    <NewPasswordForm
      title="Choose your own password"
      intro="This account was set up with a temporary password. Pick a new one to carry on."
      needsCurrent
      onSubmit={async (next, current) => onDone(await changePassword(current, next))}
    />
  );
};
